// NOTE
// - these sagas deal and manage the logged in user account
// - general user management and retrieval is found in the userSagas.js

import { takeLatest, call, put, select, all, cancel } from "redux-saga/effects";
import { subscribe } from "pusher-redux";

import {
  getEntitySaga,
  updateEntitySaga,
  createEntitySaga
} from "./apiSagas.js";
import {
  setDefaultMomentTimezoneSaga,
  applyTimezoneSaga
} from "./timezoneSagas.js";

import accountSelectors from "../reducers/account/accountSelectors.js";

import { createAction } from "../actions/actionUtils.js";

import { ACCOUNT } from "../reducers/account/accountConstants.js";
import { PUSHER } from "../../constants/pusher.js";
import { NOTIFICATIONS } from "../reducers/notifications/notificationConstants.js";
import {
  PASSWORD,
  PASSWORD_REQUEST,
  PASSWORD_RESET,
  USERNAME_RECOVERY,
  USERNAME,
  VERIFICATION_RECOVERY,
  VERIFY
} from "../actions/actionConstants.js";
import { sshKeySaga } from "./sshKeySagas.js";
import { getAccount } from "../reducers/account/accountActions.js";

// -- SAGAS -- //

export function* accountSaga(options, action) {
  // console.log("accountSaga", options, action);
  const userId = yield select(accountSelectors.selectUserSub);

  let userAction = {};
  if (action) {
    userAction = {
      ...action,
      payload: {
        ...action.payload,
        "@id": userId
      }
    };
  }

  if (userId) {
    const account = yield call(getEntitySaga, ACCOUNT, options, userAction);

    yield applyTimezoneSaga(account.timezone);

    subscribe(
      `private-${account.id}`,
      PUSHER.EVENTS.NOTIFICATION,
      NOTIFICATIONS.PUSHER_UPDATE
    );

    yield put(createAction(ACCOUNT.GET_COMPLETE));

    return account;
  }

  // forcing this as an HTTP error for now, to be handled by current error modals etc.
  throw {
    error: new Response(null, {
      status: 404,
      statusText: "No user id sub was found"
    })
  };
}

export function* updateAccountSaga(action) {
  const { sshKeys } = action.payload;

  const userId = yield select(accountSelectors.selectUserSub);

  let calls = {};

  calls["account"] = call(updateEntitySaga, ACCOUNT, undefined, action);

  if (sshKeys) {
    calls["sshKeys"] = call(sshKeySaga, {
      payload: { userId, sshKeys }
    });
  }

  calls["defaultTimezone"] = call(setDefaultMomentTimezoneSaga);

  try {
    yield all(calls);
    // whatever happens we have to complete the project put or the SPA will hang
    yield put(createAction(ACCOUNT.PUT_COMPLETE));

    // GET the project once all updates are complete
    // it's cleaner than trying to update the reducer with all of the various returns that happen here
    yield put(getAccount());
  } catch (error) {
    // whatever happens we have to complete the project put or the SPA will hang
    yield put(createAction(ACCOUNT.PUT_COMPLETE, { success: false }));
    yield cancel();
  }
}

// -- WATCHERS -- //

export function* watchAccountSagas() {
  yield takeLatest(ACCOUNT.GET, accountSaga, undefined);
  yield takeLatest(ACCOUNT.PUT, updateAccountSaga);

  yield takeLatest(PASSWORD_REQUEST, createEntitySaga, PASSWORD, {
    showErrorModal: true
  });

  yield takeLatest(PASSWORD_RESET, createEntitySaga, PASSWORD, {
    showErrorModal: false
  });

  yield takeLatest(VERIFICATION_RECOVERY, createEntitySaga, VERIFY, {
    showErrorModal: true
  });

  yield takeLatest(USERNAME_RECOVERY, createEntitySaga, USERNAME, {
    showErrorModal: true
  });
}
